/**
 * Element identity: the eight base elements, the two combined ones, their
 * opposites, cast-priority and form (DESIGN 2.1).
 */

export type Element = 'water'|'life'|'shield'|'cold'|'lightning'|'arcane'|'earth'|'fire'|'steam'|'ice'|'mud'|'lava'|'spring'|WovenElement;
/** Fused elements never come from a key press; they form in the queue (queue.ts FUSIONS). */
export type FusedElement = 'steam'|'ice'|'mud'|'lava'|'spring';
/**
 * Woven pieces (docs/spells-depth.md): made with the weave key from the last two
 * pieces in the queue (weaves.ts). Stable: nothing cancels or degrades them.
 */
export type WovenElement = 'plasma'|'flux'|'stillness'|'obsidian'|'ether'|'cloud'|'ash'|'rime'|'moment'|'dragonfire'|'stormglass'|'thunderhead'|'time'
  // slice 2
  |'tide'|'pulse'|'void'|'wildfire'|'whirlpool'|'deluge'|'inferno'|'glacier'|'soul'|ConceptElement;
/** Tier 5 (docs/spells-depth.md 2.6): ideas, not elements. They always lead the cast and invoke (resolve.ts). */
export type ConceptElement = 'sun'|'winter'|'death';
export type BaseElement = Exclude<Element, FusedElement|WovenElement>;

/** Physical-position key codes (KeyboardEvent.code) -> element (DESIGN 6.1). */
export const ELEMENT_KEYS: Record<string, BaseElement> = {
  KeyQ:'water', KeyW:'life', KeyE:'shield', KeyR:'cold', KeyA:'lightning', KeyS:'arcane', KeyD:'earth', KeyF:'fire' };

/** Pressing x removes the most recently queued OPPOSITES[x] member (DESIGN 2.2 step 4). */
export const OPPOSITES: Record<Element, Element[]> = {
  water:['lightning'], life:['arcane'], shield:['shield'], cold:['fire','steam'], lightning:['earth','water','mud','lava','spring'],
  arcane:['life','spring'], earth:['lightning'], fire:['cold','ice'], steam:['cold'], ice:['fire'],
  // Fusions inherit the opposites of their parts (lightning cancels anything with earth or water in it).
  mud:['lightning'], lava:['lightning','cold'], spring:['lightning','arcane'],
  // Woven pieces are stable: no key cancels them.
  plasma:[], flux:[], stillness:[], obsidian:[], ether:[], cloud:[], ash:[], rime:[], moment:[], dragonfire:[], stormglass:[], thunderhead:[], time:[],
  tide:[], pulse:[], void:[], wildfire:[], whirlpool:[], deluge:[], inferno:[], glacier:[], soul:[], sun:[], winter:[], death:[] };

/**
 * Highest priority element in the queue decides the spell's form (DESIGN 2.4).
 * Woven pieces rank by body: glass bolts over earth, fields over ice, their
 * beams over life and arcane, their sprays over steam, Plasma's arc over lightning.
 * The rolling Glacier sits with the glass; Tide's wave and Void's orb with the
 * fields. Concepts outrank Shield (8): they always lead.
 */
export const PRIORITY: Record<Element, number> = { shield:7, lava:6, earth:6, ice:5, life:4, arcane:4, steam:3, mud:3, spring:3, lightning:2, fire:1, cold:1, water:1,
  obsidian:6.5, stormglass:6.5, stillness:5.5, cloud:5.5, moment:5.5, thunderhead:5.5, time:5.5, flux:4.5, ether:4.5, dragonfire:4.5, ash:3.5, rime:3.5, plasma:2.5,
  glacier:6.5, tide:5.5, void:5.5, whirlpool:5.5, deluge:5.5, soul:4.5, inferno:3.6, wildfire:3.4, pulse:2.5, sun:8, winter:8, death:8 };

/** `field`: a zone placed ahead of the caster, or one that rolls away from it (woven pieces only). `concept`: tier 5, it invokes. */
export type Form = 'shield'|'projectile'|'beam'|'spray'|'arc'|'field'|'concept'|'none';
export const FORM_OF: Record<Element, Form> = { shield:'shield', earth:'projectile', ice:'projectile', life:'beam', arcane:'beam', steam:'spray', lightning:'arc', fire:'spray', cold:'spray', water:'spray', mud:'spray', lava:'projectile', spring:'spray',
  obsidian:'projectile', stormglass:'projectile', stillness:'field', cloud:'field', moment:'field', thunderhead:'field', time:'field', flux:'beam', ether:'beam', dragonfire:'beam', ash:'spray', rime:'spray', plasma:'arc',
  tide:'field', pulse:'arc', void:'field', wildfire:'spray', whirlpool:'field', deluge:'field', inferno:'spray', glacier:'field', soul:'beam', sun:'concept', winter:'concept', death:'concept' };

export const MAX_QUEUE = 5;

/** The eight base elements in keyboard order Q W E R A S D F. */
export const ELEMENT_LIST: readonly BaseElement[] = ['water', 'life', 'shield', 'cold', 'lightning', 'arcane', 'earth', 'fire'];

/** Fused elements in display order. */
export const FUSED_LIST: readonly FusedElement[] = ['steam', 'ice', 'mud', 'lava', 'spring'];

/** Woven pieces in book order: tier 2, then 3, then 4, then the concepts. */
export const WOVEN_LIST: readonly WovenElement[] = [
  'plasma', 'flux', 'stillness', 'tide', 'pulse', 'void', 'wildfire',
  'obsidian', 'ether', 'cloud', 'ash', 'rime', 'moment', 'dragonfire', 'whirlpool',
  'stormglass', 'thunderhead', 'time', 'deluge', 'inferno', 'glacier', 'soul',
  'sun', 'winter', 'death',
];

const CONCEPT_SET: ReadonlySet<string> = new Set<string>(['sun', 'winter', 'death']);

/** True for a tier 5 concept. */
export function isConcept(e: Element): e is ConceptElement {
  return CONCEPT_SET.has(e);
}

/** All elements: the base eight, the fused ones, then the woven ones. */
export const ALL_ELEMENTS: readonly Element[] = [...ELEMENT_LIST, ...FUSED_LIST, ...WOVEN_LIST];

const WOVEN_SET: ReadonlySet<string> = new Set<string>(WOVEN_LIST);

/** True for a piece made with the weave key. */
export function isWoven(e: Element): e is WovenElement {
  return WOVEN_SET.has(e);
}

const BASE_SET: ReadonlySet<string> = new Set<string>(ELEMENT_LIST);
const ALL_SET: ReadonlySet<string> = new Set<string>(ALL_ELEMENTS);

/** True for the eight elements a player can press; false for fused ones. */
export function isBase(e: Element): e is BaseElement {
  return BASE_SET.has(e);
}

/** Runtime check for untrusted input (protocol frames, storage). */
export function isElement(x: unknown): x is Element {
  return typeof x === 'string' && ALL_SET.has(x);
}

/** Runtime check for untrusted input: one of the eight pressable elements. */
export function isBaseElement(x: unknown): x is BaseElement {
  return typeof x === 'string' && BASE_SET.has(x);
}
